import { apiRequest } from '@/api/core/client';
import type {
  CalendarBootstrapPayload,
  CalendarColorUpdateInput,
  CalendarEventCreateInput,
  CalendarEventDeleteInput,
  CalendarEventModel,
  CalendarEventUpdateInput,
  CalendarEventsResponse,
  CalendarListEntry,
  CalendarNotificationSetting,
  CalendarReminder,
  CalendarRange,
  CalendarSearchOptions,
  CalendarWeatherResponse,
  WeatherUnitsSystem,
} from '@/types/calendar';

type CalendarQueryOptions = {
  timeMin?: string;
  timeMax?: string;
  calendarUids?: string[];
  includeHidden?: boolean;
  timeZone?: string | null;
  accountId?: string | null;
};

type CalendarEventsQuery = {
  calendarUids: string[];
  timeMin?: string;
  timeMax?: string;
  singleEvents?: boolean;
  showDeleted?: boolean;
  query?: string | null;
  maxResults?: number;
  timeZone?: string | null;
  accountId?: string | null;
};

function toStringValue(value: unknown): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  const result = String(value);
  return result.length ? result : null;
}

function toNumberValue(value: unknown): number | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toBooleanValue(value: unknown, fallback = false): boolean {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'no', 'off'].includes(normalized)) {
    return false;
  }
  return fallback;
}

function mapReminder(input: unknown): CalendarReminder | null {
  if (!input || typeof input !== 'object') {
    return null;
  }
  const data = input as Record<string, unknown>;
  return {
    method: toStringValue(data.method),
    minutes: toNumberValue(data.minutes),
  };
}

function mapCalendarListEntry(raw: unknown): CalendarListEntry | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const data = raw as Record<string, unknown>;
  const uid = toStringValue(data.uid) ?? `${toStringValue(data.accountId) ?? '0'}:${toStringValue(data.id) ?? crypto.randomUUID()}`;
  return {
    id: toStringValue(data.id),
    uid,
    accountId: toStringValue(data.accountId) ?? '0',
    accountEmail: toStringValue(data.accountEmail),
    summary: toStringValue(data.summary) ?? 'Calendar',
    description: toStringValue(data.description),
    timezone: toStringValue(data.timezone),
    backgroundColor: toStringValue(data.backgroundColor),
    foregroundColor: toStringValue(data.foregroundColor),
    colorId: toStringValue(data.colorId),
    primary: toBooleanValue(data.primary, false),
    selected: toBooleanValue(data.selected, true),
    hidden: toBooleanValue(data.hidden, false),
    accessRole: toStringValue(data.accessRole) ?? 'reader',
    readOnly: toBooleanValue(data.readOnly, false),
    canEdit: toBooleanValue(data.canEdit, false),
    conferenceProperties: (data.conferenceProperties as Record<string, unknown> | null) ?? null,
    defaultReminders: Array.isArray(data.defaultReminders)
      ? data.defaultReminders.map(mapReminder).filter(Boolean) as CalendarReminder[]
      : [],
    notificationSettings: Array.isArray(data.notificationSettings)
      ? (data.notificationSettings
          .map((entry) => {
            if (!entry || typeof entry !== 'object') {
              return null;
            }
            const notif = entry as Record<string, unknown>;
            return {
              type: toStringValue(notif.type),
              method: toStringValue(notif.method),
            } satisfies CalendarNotificationSetting;
          })
          .filter(Boolean) as CalendarNotificationSetting[])
      : [],
    location: toStringValue(data.location),
    summaryOverride: toStringValue(data.summaryOverride),
    backgroundImageLink: toStringValue(data.backgroundImageLink),
  };
}

function mapDateTime(raw: unknown) {
  if (!raw || typeof raw !== 'object') {
    return {
      date: null,
      dateTime: null,
      timeZone: null,
    };
  }
  const data = raw as Record<string, unknown>;
  return {
    date: toStringValue(data.date),
    dateTime: toStringValue(data.dateTime),
    timeZone: toStringValue(data.timeZone),
  };
}

function mapAttachment(raw: unknown) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const data = raw as Record<string, unknown>;
  return {
    fileUrl: toStringValue(data.fileUrl),
    fileId: toStringValue(data.fileId),
    title: toStringValue(data.title),
    iconLink: toStringValue(data.iconLink),
    mimeType: toStringValue(data.mimeType),
  };
}

function mapParticipant(raw: unknown) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const data = raw as Record<string, unknown>;
  return {
    email: toStringValue(data.email),
    displayName: toStringValue(data.displayName),
    self: toBooleanValue(data.self, false),
    resource: toBooleanValue(data.resource, false),
    optional: toBooleanValue(data.optional, false),
    responseStatus: toStringValue(data.responseStatus),
    comment: toStringValue(data.comment),
    additionalGuests: toNumberValue(data.additionalGuests),
  };
}

function deriveDateLabel(value: { date: string | null; dateTime: string | null }, fallbackTimestamp: number | null) {
  if (value.date) {
    return value.date;
  }
  if (value.dateTime) {
    const parsed = Date.parse(value.dateTime);
    if (Number.isFinite(parsed)) {
      return new Date(parsed).toISOString().slice(0, 10);
    }
  }
  if (fallbackTimestamp && Number.isFinite(fallbackTimestamp)) {
    return new Date(fallbackTimestamp).toISOString().slice(0, 10);
  }
  return null;
}

function inferTimestamp(value: { date?: string | null; dateTime?: string | null }): number | null {
  if (value.dateTime) {
    const parsed = Date.parse(value.dateTime);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (value.date) {
    const parsed = Date.parse(`${value.date}T00:00:00Z`);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function mapCalendarEvent(raw: unknown): CalendarEventModel | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const data = raw as Record<string, unknown>;
  const start = mapDateTime(data.start);
  const end = mapDateTime(data.end);
  const originalStartTime = data.originalStartTime ? mapDateTime(data.originalStartTime) : null;
  const remindersRaw = data.reminders && typeof data.reminders === 'object'
    ? (data.reminders as Record<string, unknown>)
    : null;
  const overrides = Array.isArray(remindersRaw?.overrides)
    ? remindersRaw!.overrides.map(mapReminder).filter(Boolean) as CalendarReminder[]
    : [];
  const startTimestamp = inferTimestamp(start);
  const endTimestamp = inferTimestamp(end);

  return {
    id: toStringValue(data.id) ?? crypto.randomUUID(),
    calendarId: toStringValue(data.calendarId) ?? toStringValue(data.calendar_id) ?? null,
    calendarUid: toStringValue(data.calendarUid) ?? toStringValue(data.calendar_uid) ?? 'calendar:unknown',
    accountId: toStringValue(data.accountId) ?? '0',
    status: toStringValue(data.status),
    summary: toStringValue(data.summary),
    description: toStringValue(data.description),
    location: toStringValue(data.location),
    htmlLink: toStringValue(data.htmlLink),
    hangoutLink: toStringValue(data.hangoutLink),
    createdAt: toStringValue(data.createdAt),
    updatedAt: toStringValue(data.updatedAt),
    start,
    end,
    originalStartTime,
    allDay: toBooleanValue(data.allDay, Boolean(start.date && !start.dateTime)),
    recurrence: Array.isArray(data.recurrence)
      ? data.recurrence.map((value) => toStringValue(value)).filter(Boolean) as string[]
      : [],
    recurringEventId: toStringValue(data.recurringEventId),
    iCalUid: toStringValue(data.iCalUid),
    sequence: toNumberValue(data.sequence),
    transparency: toStringValue(data.transparency),
    visibility: toStringValue(data.visibility),
    eventType: toStringValue(data.eventType),
    organizer: mapParticipant(data.organizer),
    creator: mapParticipant(data.creator),
    attendees: Array.isArray(data.attendees)
      ? (data.attendees
          .map(mapParticipant)
          .filter((participant): participant is NonNullable<ReturnType<typeof mapParticipant>> => Boolean(participant)))
      : [],
    attendeesOmitted: toBooleanValue(data.attendeesOmitted, false),
    extendedProperties: (data.extendedProperties as Record<string, unknown> | null) ?? null,
    reminders: {
      useDefault: toBooleanValue(remindersRaw?.useDefault, true),
      overrides,
    },
    color: {
      colorId: toStringValue((data.color as Record<string, unknown> | null)?.colorId ?? data.colorId),
      backgroundColor: toStringValue((data.color as Record<string, unknown> | null)?.backgroundColor ?? data.backgroundColor),
      foregroundColor: toStringValue((data.color as Record<string, unknown> | null)?.foregroundColor ?? data.foregroundColor),
    },
    anyoneCanAddSelf: toBooleanValue(data.anyoneCanAddSelf, false),
    guestsCanInviteOthers: data.guestsCanInviteOthers === undefined ? null : toBooleanValue(data.guestsCanInviteOthers, false),
    guestsCanModify: data.guestsCanModify === undefined ? null : toBooleanValue(data.guestsCanModify, false),
    guestsCanSeeOtherGuests: data.guestsCanSeeOtherGuests === undefined ? null : toBooleanValue(data.guestsCanSeeOtherGuests, true),
    privateCopy: data.privateCopy === undefined ? null : toBooleanValue(data.privateCopy, false),
    locked: data.locked === undefined ? null : toBooleanValue(data.locked, false),
    source: (data.source as Record<string, unknown> | null) ?? null,
    attachments: Array.isArray(data.attachments)
      ? (data.attachments
          .map(mapAttachment)
          .filter((attachment): attachment is NonNullable<ReturnType<typeof mapAttachment>> => Boolean(attachment)))
      : [],
    conferenceData: (data.conferenceData as Record<string, unknown> | null) ?? null,
    startTimestamp,
    endTimestamp,
    startDate: deriveDateLabel(start, startTimestamp),
    endDate: deriveDateLabel(end, endTimestamp),
  };
}

function mapRange(raw: unknown): CalendarRange {
  if (!raw || typeof raw !== 'object') {
    return {
      timeMin: new Date().toISOString(),
      timeMax: new Date().toISOString(),
    };
  }
  const data = raw as Record<string, unknown>;
  return {
    timeMin: toStringValue(data.timeMin) ?? toStringValue(data.time_min) ?? new Date().toISOString(),
    timeMax: toStringValue(data.timeMax) ?? toStringValue(data.time_max) ?? new Date().toISOString(),
  };
}

function mapBootstrapResponse(raw: unknown): CalendarBootstrapPayload {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const calendars = Array.isArray(data.calendars)
    ? data.calendars.map(mapCalendarListEntry).filter(Boolean) as CalendarListEntry[]
    : [];
  const events = Array.isArray(data.events)
    ? data.events.map(mapCalendarEvent).filter(Boolean) as CalendarEventModel[]
    : [];
  return {
    calendars,
    events,
    range: mapRange(data.range),
    timezone: toStringValue(data.timezone),
    generatedAt: toStringValue(data.generatedAt) ?? new Date().toISOString(),
    defaultCalendarUids: Array.isArray(data.defaultCalendarUids)
      ? (data.defaultCalendarUids.map((value) => toStringValue(value)).filter(Boolean) as string[])
      : [],
  };
}

function mapEventsResponse(raw: unknown): CalendarEventsResponse {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const events = Array.isArray(data.events)
    ? data.events.map(mapCalendarEvent).filter(Boolean) as CalendarEventModel[]
    : [];
  const nextPageTokens: Record<string, string> = {};
  if (data.nextPageTokens && typeof data.nextPageTokens === 'object') {
    Object.entries(data.nextPageTokens as Record<string, unknown>).forEach(([key, value]) => {
      const stringValue = toStringValue(value);
      if (stringValue) {
        nextPageTokens[key] = stringValue;
      }
    });
  }
  return {
    events,
    range: mapRange(data.range),
    nextPageTokens,
  };
}

function buildQuery(options: CalendarQueryOptions = {}) {
  const calendarUids = options.calendarUids && options.calendarUids.length ? options.calendarUids.join(',') : undefined;
  return {
    timeMin: options.timeMin,
    timeMax: options.timeMax,
    calendar_uids: calendarUids,
    includeHidden: options.includeHidden,
    timeZone: options.timeZone ?? undefined,
    account_id: options.accountId ?? undefined,
  };
}

export async function fetchCalendarBootstrap(options: CalendarQueryOptions = {}): Promise<CalendarBootstrapPayload> {
  const response = await apiRequest<unknown>({
    path: '/calendar/bootstrap',
    query: buildQuery(options),
  });
  return mapBootstrapResponse(response);
}

export async function fetchCalendars(options: CalendarQueryOptions = {}): Promise<CalendarListEntry[]> {
  const response = await apiRequest<{ calendars?: unknown[] }>({
    path: '/calendar/calendars',
    query: {
      includeHidden: options.includeHidden,
      account_id: options.accountId ?? undefined,
    },
  });
  return Array.isArray(response.calendars)
    ? (response.calendars.map(mapCalendarListEntry).filter(Boolean) as CalendarListEntry[])
    : [];
}

function assertEventResponse(response: { event?: CalendarEventModel | null } | null): CalendarEventModel {
  if (response && response.event) {
    return response.event;
  }
  throw new Error('Calendar event response was malformed');
}

export async function createCalendarEvent(input: CalendarEventCreateInput): Promise<CalendarEventModel> {
  const response = await apiRequest<{ event?: CalendarEventModel | null }>({
    path: '/calendar/events',
    method: 'POST',
    body: {
      calendarUid: input.calendarUid,
      event: input.event,
      sendUpdates: input.sendUpdates,
      supportsAttachments: input.supportsAttachments,
    },
  });
  return assertEventResponse(response);
}

export async function updateCalendarEvent(input: CalendarEventUpdateInput): Promise<CalendarEventModel> {
  const response = await apiRequest<{ event?: CalendarEventModel | null }>({
    path: `/calendar/events/${encodeURIComponent(input.calendarUid)}/${encodeURIComponent(input.eventId)}`,
    method: 'PATCH',
    body: {
      calendarUid: input.calendarUid,
      eventId: input.eventId,
      event: input.event,
      sendUpdates: input.sendUpdates,
      supportsAttachments: input.supportsAttachments,
    },
  });
  return assertEventResponse(response);
}

export async function deleteCalendarEvent(input: CalendarEventDeleteInput): Promise<void> {
  await apiRequest<null>({
    path: `/calendar/events/${encodeURIComponent(input.calendarUid)}/${encodeURIComponent(input.eventId)}`,
    method: 'DELETE',
    body: {
      calendarUid: input.calendarUid,
      eventId: input.eventId,
      sendUpdates: input.sendUpdates,
    },
  });
}

export async function updateCalendarColor(input: CalendarColorUpdateInput): Promise<CalendarListEntry> {
  const response = await apiRequest<{ calendar?: CalendarListEntry | null }>({
    path: `/calendar/calendars/${encodeURIComponent(input.calendarUid)}/color`,
    method: 'PATCH',
    body: {
      colorId: input.colorId,
      backgroundColor: input.backgroundColor,
      foregroundColor: input.foregroundColor,
    },
  });
  if (response?.calendar) {
    return response.calendar;
  }
  throw new Error('Calendar color update failed');
}

export async function searchCalendarEvents(options: CalendarSearchOptions): Promise<{ events: CalendarEventModel[]; range: CalendarRange }> {
  if (!options.calendarUids?.length) {
    return { events: [], range: { timeMin: options.timeMin ?? new Date().toISOString(), timeMax: options.timeMax ?? new Date().toISOString() } };
  }
  const response = await apiRequest<{ events?: CalendarEventModel[]; range?: CalendarRange }>({
    path: '/calendar/search',
    query: {
      calendar_uids: options.calendarUids.join(','),
      q: options.query ?? undefined,
      attendee: options.attendee ?? undefined,
      timeMin: options.timeMin,
      timeMax: options.timeMax,
      maxResults: options.maxResults,
    },
  });
  return {
    events: Array.isArray(response.events) ? response.events : [],
    range: response.range ?? { timeMin: options.timeMin ?? new Date().toISOString(), timeMax: options.timeMax ?? new Date().toISOString() },
  };
}

export async function fetchCalendarEvents(options: CalendarEventsQuery): Promise<CalendarEventsResponse> {
  if (!options.calendarUids?.length) {
    return { events: [], range: { timeMin: options.timeMin ?? new Date().toISOString(), timeMax: options.timeMax ?? new Date().toISOString() } };
  }
  const response = await apiRequest<unknown>({
    path: '/calendar/events',
    query: {
      calendar_uids: options.calendarUids.join(','),
      timeMin: options.timeMin,
      timeMax: options.timeMax,
      singleEvents: options.singleEvents ?? true,
      showDeleted: options.showDeleted,
      q: options.query ?? undefined,
      maxResults: options.maxResults,
      timeZone: options.timeZone ?? undefined,
      account_id: options.accountId ?? undefined,
    },
  });
  return mapEventsResponse(response);
}

export interface CalendarWeatherRequest {
  lat: number;
  lng: number;
  units?: WeatherUnitsSystem;
  days?: number;
  languageCode?: string;
  signal?: AbortSignal;
}

export async function fetchDailyWeatherForecast(options: CalendarWeatherRequest): Promise<CalendarWeatherResponse> {
  const query: Record<string, string> = {
    lat: String(options.lat),
    lng: String(options.lng),
  };
  if (options.units) {
    query.units = options.units;
  }
  if (options.days) {
    query.days = String(options.days);
  }
  if (options.languageCode) {
    query.languageCode = options.languageCode;
  }
  return apiRequest<CalendarWeatherResponse>({
    path: '/calendar/weather/daily',
    query,
    signal: options.signal,
  });
}
